import {
  Box,
  Button,
  CloseButton,
  Divider,
  Heading,
  HStack,
  Icon,
  ListItem,
  OrderedList,
  Stack,
  StackDivider,
  Text,
  UseDisclosureProps,
  VStack,
} from '@chakra-ui/react';
import { FC, useEffect, useState } from 'react';
import {
  useAppContext,
  useGetTeamCaptains,
  useUpdateTeamCaptains,
} from '@frx/frx-react-context';
import { Captain, Participant } from '@frx/frx-services';
import { Modal } from '../../../../../components';
import {
  ChevronDown,
  ChevronRight,
  Partner,
  Plus,
  StarFilled,
  StarOutline,
} from '../../../../../assets';
import { PromoteCoCaptain } from './PromoteCoCaptain';
import { useEventId, useTeamId } from '../../../../../hooks';

export interface ManageCoCaptainsProps
  extends Required<Pick<UseDisclosureProps, 'isOpen' | 'onClose'>> {
  teamMembers: Participant[];
}
export const ManageCoCaptains: FC<ManageCoCaptainsProps> = ({
  teamMembers,
  isOpen,
  onClose,
}) => {
  const eventId = useEventId();
  const teamId = useTeamId();
  const [expand, setExpand] = useState(false);
  const [coCaptainView, setCoCaptainView] = useState(false);
  const [confirmPanel, setConfirmPanel] = useState(-1);
  const {
    data: {
      authData: { user },
    },
  } = useAppContext();
  const { data: captains } = useGetTeamCaptains({
    team_id: teamId,
    fr_id: eventId,
  });
  const { mutate: removeCoCaptain } = useUpdateTeamCaptains();
  const captainConsIds = captains?.captain.map(
    (captain: Captain) => captain.consId
  );

  useEffect(() => {
    setConfirmPanel(-1);
  }, [captains]);

  const handleRemoveCaptain = (id: string) => {
    const captainsBody = captainConsIds?.filter((cid) => cid !== id);
    removeCoCaptain({
      fr_id: eventId,
      captains: JSON.stringify(captainsBody).replaceAll(/[-[\]\s]/g, ''),
      team_id: teamId,
    });
  };

  const isMainCaptain = (teamConsId: number) => user?.cons_id === teamConsId;

  const handleExpand = () => setExpand((prevState) => !prevState);

  const handleCoCaptainView = () => setCoCaptainView((prevState) => !prevState);

  const handleModalCLose = () => {
    onClose();
    setExpand(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleModalCLose}>
      {!coCaptainView && (
        <Stack px={2} spacing={4}>
          <VStack color="brand.primary.swatch1">
            <Icon as={Partner} boxSize={16} />
            <Heading fontSize="24px">Manage co-captains</Heading>
          </VStack>
          <Text fontSize="md" color="brand.monochromatic.700" align="left">
            Here you can promote and remove team co-captains.
          </Text>
          <Box alignSelf="flex-start">
            <Button
              p={0}
              variant="ghost"
              colorScheme="blue"
              fontWeight="bold"
              leftIcon={!expand ? <ChevronRight /> : <ChevronDown />}
              onClick={handleExpand}
            >
              What can a co-captain do?
            </Button>
            {expand && (
              <OrderedList spacing={1}>
                <ListItem>
                  <Text
                    fontSize="md"
                    color="brand.monochromatic.700"
                    align="left"
                  >
                    Edit team page custom URL
                  </Text>
                </ListItem>
                <ListItem>
                  <Text
                    fontSize="md"
                    color="brand.monochromatic.700"
                    align="left"
                  >
                    Edit team name
                  </Text>
                </ListItem>
                <ListItem>
                  <Text
                    fontSize="md"
                    color="brand.monochromatic.700"
                    align="left"
                  >
                    Add & remove team captains
                  </Text>
                </ListItem>
                <ListItem>
                  <Text
                    fontSize="md"
                    color="brand.monochromatic.700"
                    align="left"
                  >
                    Change team’s fundraising goal
                  </Text>
                </ListItem>
              </OrderedList>
            )}
          </Box>
          <Box pt={2} w="full">
            <VStack
              align="stretch"
              spacing={4}
              divider={<StackDivider color="brand.primary.lightGrey" />}
            >
              {captains?.captain.map((cap, index) => (
                <Box key={cap.consId}>
                  <HStack justify="space-between" px={4}>
                    <HStack>
                      <Icon
                        as={
                          isMainCaptain(Number(cap.consId))
                            ? StarFilled
                            : StarOutline
                        }
                        color="brand.secondary.swatch4"
                      />
                      <Text fontSize="md" color="brand.additional.black">
                        {cap.name.first} {cap.name.last}
                      </Text>
                    </HStack>
                    {!isMainCaptain(Number(cap.consId)) && (
                      <CloseButton
                        onClick={() => setConfirmPanel(index)}
                        color="brand.secondary.swatch4"
                      />
                    )}
                  </HStack>
                  {confirmPanel === index && (
                    <VStack pt={4}>
                      <Text
                        fontSize="md"
                        color="brand.monochromatic.700"
                        align="left"
                      >
                        Are you sure you want to remove this person’s co-captain
                        status?
                      </Text>
                      <Button
                        w="full"
                        onClick={() => handleRemoveCaptain(cap.consId)}
                      >
                        Remove co-captain
                      </Button>
                    </VStack>
                  )}
                </Box>
              ))}
            </VStack>
            <Box px={4} py={2}>
              <Divider color="brand.monochromatic.700" />
              <Button
                variant="ghost"
                leftIcon={<Plus />}
                color="brand.monochromatic.700"
                p={0}
                onClick={() => setCoCaptainView(true)}
              >
                Add a co-captain
              </Button>
            </Box>
          </Box>
        </Stack>
      )}

      {coCaptainView && (
        <PromoteCoCaptain
          teamMembers={teamMembers}
          captainConsIds={captainConsIds || []}
          onClose={handleCoCaptainView}
        />
      )}
    </Modal>
  );
};
